import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Chip,
  FormHelperText,
  IconButton,
  Divider,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth/authService';
import PermissionScopeSelector from './PermissionScopeSelector';
import NetworkSectionSelector from './NetworkSectionSelector';

const CreateUserModal = ({ open, onClose, onUserCreated }) => {
  const { user: currentUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignableResources, setAssignableResources] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    role: 'viewer',
    permissionScope: 'organization',
    permissions: []
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  const steps = [
    { label: 'User Details', icon: <PersonIcon /> },
    { label: 'Role & Permissions', icon: <SecurityIcon /> },
    { label: 'Resource Assignment', icon: <AssignmentIcon /> },
    { label: 'Review & Create', icon: <CheckIcon /> }
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        username: '',
        email: '',
        fullName: '',
        role: 'viewer',
        permissionScope: 'organization',
        permissions: []
      });
      setActiveStep(0);
      setError(null);
      setErrors({});
      fetchAssignableResources();
    }
  }, [open]);

  const fetchAssignableResources = async () => {
    try {
      const response = await authService.getAssignableResources();
      if (response.success) {
        setAssignableResources(response.resources);
      }
    } catch (error) {
      console.error('Error fetching assignable resources:', error);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // User Details
        if (!formData.username.trim()) {
          newErrors.username = 'Username is required';
        } else if (formData.username.length < 3) {
          newErrors.username = 'Username must be at least 3 characters';
        }
        
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Full name is required';
        }
        break;

      case 1: // Role & Permissions
        if (!formData.role) {
          newErrors.role = 'Role is required';
        }
        if (!formData.permissionScope) {
          newErrors.permissionScope = 'Permission scope is required';
        }
        break;

      case 2: // Resource Assignment
        if (formData.permissionScope === 'network_section' && formData.permissions.length === 0) {
          newErrors.permissions = 'Please select at least one network or section for network/section level access';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Reset permissions if changing permission scope
    if (field === 'permissionScope') {
      setFormData(prev => ({
        ...prev,
        permissions: []
      }));
    }
  };

  const handlePermissionsChange = (permissions) => {
    setFormData(prev => ({
      ...prev,
      permissions
    }));

    if (errors.permissions) {
      setErrors(prev => ({
        ...prev,
        permissions: null
      }));
    }
  };

  const handleCreate = async () => {
    if (!validateStep(2)) return;

    try {
      setLoading(true);
      setError(null);

      // Transform permissions to backend format
      const transformedPermissions = formData.permissions.map(permission => {
        const basePermission = {
          permissionType: formData.role === 'inspector' ? 'write' : 'read',
          organizationId: currentUser?.organizationId
        };

        if (permission.type === 'network') {
          return {
            ...basePermission,
            networkId: permission.id,
            sectionId: null
          };
        } else if (permission.type === 'section') {
          return {
            ...basePermission,
            networkId: permission.networkId,
            sectionId: permission.id
          };
        }
        
        return basePermission;
      });

      const userDataToSend = {
        ...formData,
        permissions: transformedPermissions
      };

      const response = await authService.createUser(userDataToSend);

      if (response.success) {
        // Show temporary password for testing (since email isn't set up)
        if (response.tempPassword) {
          alert(`User created successfully!\n\nTemporary Password: ${response.tempPassword}\n\nUsername: ${formData.username}\n\nThe user should change this password on first login.`);
        }
        
        onUserCreated(response.user);
        onClose();
      } else {
        setError(response.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              error={!!errors.username}
              helperText={errors.username || 'Must be unique across the system'}
              placeholder="e.g., john.doe"
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email || 'Will receive invitation and notifications'}
              placeholder="e.g., john.doe@company.com"
            />
            <TextField
              fullWidth
              label="Full Name"
              value={formData.fullName}
              onChange={(e) => handleChange('fullName', e.target.value)}
              error={!!errors.fullName}
              helperText={errors.fullName}
              placeholder="e.g., John Doe"
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <FormControl fullWidth error={!!errors.role}>
              <InputLabel>User Role</InputLabel>
              <Select
                value={formData.role}
                label="User Role"
                onChange={(e) => handleChange('role', e.target.value)}
              >
                <MenuItem value="inspector">Inspector</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
              {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
            </FormControl>
            
            <PermissionScopeSelector
              value={formData.permissionScope}
              onChange={(value) => handleChange('permissionScope', value)}
              error={errors.permissionScope}
              role={formData.role}
            />
            
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Role Capabilities
              </Typography>
              {formData.role === 'inspector' ? (
                <Box>
                  <Typography variant="body2" paragraph>
                    <strong>Inspectors can:</strong>
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Create and edit inspections</li>
                    <li>Record defects and findings</li>
                    <li>Upload photos and documentation</li>
                    <li>View assets and infrastructure data</li>
                    {formData.permissionScope === 'organization' 
                      ? <li>Access all organization data</li>
                      : <li>Access only assigned networks/sections</li>
                    }
                  </ul>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" paragraph>
                    <strong>Viewers can:</strong>
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>View inspections and reports</li>
                    <li>Access defect information</li>
                    <li>Download reports and documentation</li>
                    <li>View assets and infrastructure data</li>
                    {formData.permissionScope === 'organization' 
                      ? <li>Access all organization data (read-only)</li>
                      : <li>Access only assigned networks/sections (read-only)</li>
                    }
                  </ul>
                </Box>
              )}
            </Paper>
          </Stack>
        );

      case 2:
        if (formData.permissionScope === 'organization') {
          return (
            <Stack spacing={3}>
              <Alert severity="info">
                This user will have {formData.role} access to all data within your organization. 
                No specific network or section assignment is required.
              </Alert>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Organization-Level Access
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The user will automatically have access to all current and future networks 
                  and sections within your organization.
                </Typography>
              </Paper>
            </Stack>
          );
        } else {
          return (
            <Stack spacing={3}>
              <Alert severity="warning">
                Please select specific networks and/or sections this user should have access to.
              </Alert>
              <NetworkSectionSelector
                assignableResources={assignableResources}
                selectedPermissions={formData.permissions}
                onChange={handlePermissionsChange}
                error={errors.permissions}
              />
            </Stack>
          );
        }

      case 3:
        return (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Review User Details
            </Typography>
            <Divider />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.username}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formData.fullName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Role
                </Typography>
                <Chip 
                  label={formData.role.charAt(0).toUpperCase() + formData.role.slice(1)}
                  color={formData.role === 'inspector' ? 'warning' : 'info'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Permission Scope
                </Typography>
                <Chip 
                  label={formData.permissionScope === 'organization' ? 'Organization-Level' : 'Network/Section-Level'}
                  color={formData.permissionScope === 'organization' ? 'success' : 'secondary'}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              {formData.permissionScope === 'network_section' && formData.permissions.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Assigned Networks/Sections
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {formData.permissions.map((permission, index) => (
                      <Chip
                        key={index}
                        label={`${permission.name} (${permission.type})`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Grid>
              )}
            </Grid>

            <Alert severity="info">
              <Typography variant="body2">
                A temporary password will be generated and sent to the user via email. 
                They will be required to change it on first login.
              </Typography>
            </Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>Create New User</Typography>
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel icon={step.icon}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Creating user...
            </Typography>
          </Stack>
        ) : (
          renderStepContent()
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={loading}>
            Next
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleCreate} 
            disabled={loading}
            color="success"
          >
            Create User
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserModal; 